import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";
import "./examSheet.css";
import Sidebar from "./Sidebar";
import {
  FaPaperPlane,
  FaPrint,
  FaEdit,
  FaSave,
  FaTimes,
  FaCheck,
  FaCalculator,
  FaSearch,
  FaArrowUp,
  FaArrowDown,
  FaClipboardCheck,
  FaBook,
  FaCalendarAlt,
  FaUserGraduate,
  FaSpinner,
  FaExclamationTriangle,
  FaDatabase,
  FaCheckCircle,
  FaExclamationCircle,
  FaIdCard,
  FaFilter,
  FaPen,
  FaInfoCircle,
} from "react-icons/fa";

// PHP API Base URL
const PHP_API_BASE = "https://mhsdev.online/MHS-WEB/api/ManageExamSheets.php";

// Exam type mapping with IDs
const EXAM_TYPES = {
  predefined: [
    { value: "1st Assessment", label: "1st Assessment", id: 1 },
    { value: "2nd Assessment", label: "2nd Assessment", id: 2 },
    { value: "3rd Assessment", label: "3rd Assessment", id: 3 },
    { value: "Finals", label: "Finals", id: 4 },
    { value: "Mid-Term", label: "Mid-Term", id: 5 },
    { value: "Pre-Board", label: "Pre-Board", id: 6 },
    { value: "Mock Test", label: "Mock Test", id: 7 },
    { value: "Unit Test", label: "Unit Test", id: 8 },
    { value: "Quiz", label: "Quiz", id: 9 },
  ],
  custom: [{ value: "other", label: "Custom Exam", id: 99 }],
};

// Session storage key for persistence
const STORAGE_KEY = "examSheetData";

const ExamSheet = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const className = location.state?.className || "Default Class";
  const inputRefs = useRef([]);

  // Initial empty form state
  const initialFormData = {
    examType: "",
    customExamType: "",
    examTypeOption: "predefined",
    subject: "",
    totalMarks: "",
    classSection: "",
    date: new Date().toISOString().split("T")[0],
    examDay: new Date().toLocaleString("en-us", { weekday: "long" }),
    examId: "",
  };

  const [formData, setFormData] = useState(initialFormData);
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [sections, setSections] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [savedExams, setSavedExams] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [examDetails, setExamDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentFocusIndex, setCurrentFocusIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const [apiStatus, setApiStatus] = useState({
    subjects: false,
    sections: false,
    students: false,
    submit: false,
  });
  const [verificationResult, setVerificationResult] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [existingExams, setExistingExams] = useState([]);
  const [suggestedNames, setSuggestedNames] = useState([]);

  // New State for hiding the dashboard stats/controls
  const [showStats, setShowStats] = useState(false);

  // ---------- Session Storage Persistence ----------
  // Load saved data on mount
  useEffect(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Only restore if the class name matches (prevents mixing data)
        if (parsed.className === className) {
          setFormData(parsed.formData || initialFormData);
          setStudents(parsed.students || []);
          setExamDetails(parsed.examDetails || null);
          setVerificationResult(parsed.verificationResult || null);
          setExistingExams(parsed.existingExams || []);
          setSuggestedNames(parsed.suggestedNames || []);
          setIsModalOpen(parsed.isModalOpen !== undefined ? parsed.isModalOpen : true);
        }
      } catch (e) {
        console.error("Failed to restore session", e);
      }
    }
  }, [className]);

  // Debounced save to session storage
  useEffect(() => {
    const timer = setTimeout(() => {
      const dataToSave = {
        className,
        formData,
        students,
        examDetails,
        verificationResult,
        existingExams,
        suggestedNames,
        isModalOpen,
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [
    className,
    formData,
    students,
    examDetails,
    verificationResult,
    existingExams,
    suggestedNames,
    isModalOpen,
  ]);

  // Clear storage after successful submission
  const clearSavedData = () => {
    sessionStorage.removeItem(STORAGE_KEY);
  };

  // Reset form to initial state (used after submission or on explicit cancel)
  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setStudents([]);
    setExamDetails(null);
    setVerificationResult(null);
    setExistingExams([]);
    setSuggestedNames([]);
    setIsModalOpen(true);
    clearSavedData();
  }, []);

  // ---------- End of Persistence ----------

  // Function to handle API errors
  const handleApiError = (error, context = "operation") => {
    console.error(`API Error in ${context}:`, error);
    let errorMessage = error.message || "Unknown error occurred";
    if (error.response) {
      errorMessage =
        error.response.data?.error ||
        error.response.data?.message ||
        `HTTP ${error.response.status}`;
    }
    Swal.fire({
      icon: "error",
      title: "Server Error",
      text: `Failed to ${context}. ${errorMessage}`,
      confirmButtonColor: "#e53e3e",
      footer: `<small>Please check if PHP server is running at ${PHP_API_BASE}</small>`,
    });
  };

  // Function to get status color
  const getStatusColor = (status) => {
    switch (status) {
      case "success":
        return "#48bb78";
      case "loading":
        return "#4299e1";
      case "error":
        return "#e53e3e";
      case "empty":
        return "#d69e2e";
      default:
        return "#a0aec0";
    }
  };

  // Function to get status text
  const getStatusText = (status) => {
    switch (status) {
      case "success":
        return "✓ Connected";
      case "loading":
        return "⏳ Loading...";
      case "error":
        return "✗ Error";
      case "empty":
        return "⚠ No Data";
      default:
        return "--";
    }
  };

  // Generate unique exam ID
  const generateExamId = () => {
    const timestamp = new Date().getTime().toString(36);
    const random = Math.random().toString(36).substr(2, 5);
    const examId = `EXM-${timestamp}-${random}`.toUpperCase().substr(0, 12);
    setFormData((prev) => ({ ...prev, examId }));
    return examId;
  };

  // Generate suggested names when duplicate is detected
  const generateSuggestedNames = (examName) => {
    const year = new Date().getFullYear();
    const suggestions = [
      `${examName} ${year}`,
      `${examName} Retake`,
      `${examName} Makeup`,
      `${examName} Special`,
      `${examName} ${year} Session`,
      `${examName} Extra`,
      `${examName} Remedial`,
    ];
    setSuggestedNames(suggestions);
  };

  // Check if exam already exists for current year
  const checkExistingExams = async () => {
    const year = new Date(formData.date).getFullYear();
    const examType =
      formData.examTypeOption === "custom"
        ? formData.customExamType
        : formData.examType;

    try {
      const response = await axios.get(PHP_API_BASE, {
        params: {
          action: "search-exam-students",
          className: className,
          section: formData.classSection,
          subject: formData.subject,
          exam_type: examType,
          date_from: `${year}-01-01`,
          date_to: `${year}-12-31`,
        },
      });

      if (
        response.data.success &&
        response.data.data &&
        response.data.data.length > 0
      ) {
        setExistingExams(response.data.data);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error checking existing exams:", error);
      return false;
    }
  };

  // Verify exam details for duplicates
  const verifyExamDetails = async () => {
    const requiredFields = ["subject", "totalMarks", "classSection", "date"];
    const examType =
      formData.examTypeOption === "custom"
        ? formData.customExamType
        : formData.examType;

    if (formData.examTypeOption === "custom" && !formData.customExamType) {
      Swal.fire({
        title: "Missing Information",
        text: "Please enter a custom exam name",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }

    if (formData.examTypeOption === "predefined" && !formData.examType) {
      Swal.fire({
        title: "Missing Information",
        text: "Please select an exam type",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }

    const missingFields = requiredFields.filter((field) => !formData[field]);
    if (missingFields.length > 0) {
      Swal.fire({
        title: "Missing Information",
        text: `Please fill in: ${missingFields.join(", ")}`,
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }

    setIsVerifying(true);
    setVerificationResult(null);
    setExistingExams([]);
    setSuggestedNames([]);

    try {
      // Generate exam ID if not already generated
      if (!formData.examId) {
        generateExamId();
      }

      // Check for existing exams
      const examExists = await checkExistingExams();

      if (examExists && existingExams.length > 0) {
        const year = new Date(formData.date).getFullYear();
        const examTypeName =
          formData.examTypeOption === "custom"
            ? formData.customExamType
            : formData.examType;

        // Generate suggestions locally
        const suggestions = [
          `${examTypeName} ${year}`,
          `${examTypeName} Retake`,
          `${examTypeName} Makeup`,
          `${examTypeName} Special`,
          `${examTypeName} ${year} Session`,
          `${examTypeName} Extra`,
          `${examTypeName} Remedial`,
        ];
        setSuggestedNames(suggestions); // update state for later use

        setVerificationResult({
          isDuplicate: true,
          message: `"${examTypeName}" exam already exists for ${className} in ${year}.`,
          existingExams: existingExams,
          suggestions: suggestions,
        });

        Swal.fire({
          title: "⚠️ Duplicate Exam Detected",
          html: `
            <div style="text-align: left;">
              <div style="background: #fff5f5; padding: 15px; border-radius: 8px; border-left: 4px solid #e53e3e; margin-bottom: 15px;">
                <p style="color: #e53e3e; font-weight: 600; margin-bottom: 10px;">
                  Exam "${examTypeName}" already exists for ${className} in ${year}
                </p>
                <p><strong>Exam Details:</strong></p>
                <ul style="margin-left: 20px;">
                  <li>Class: ${className}</li>
                  <li>Section: ${formData.classSection}</li>
                  <li>Subject: ${formData.subject}</li>
                  <li>Year: ${year}</li>
                  <li>Existing Records: ${existingExams.length}</li>
                </ul>
              </div>
              
              <div style="background: #f7fafc; padding: 15px; border-radius: 8px; margin-bottom: 15px;">
                <p style="font-weight: 600; margin-bottom: 10px;">💡 Suggested Alternative Names:</p>
                <ul style="margin-left: 20px;">
                  ${suggestions
                    .map(
                      (name, index) => `
                    <li style="margin-bottom: 5px;">
                      <button 
                        onclick="this.parentElement.parentElement.querySelectorAll('button').forEach(btn => btn.style.background=''); this.style.background='#e2e8f0'; this.style.border='2px solid #4f46e5';" 
                        style="padding: 5px 10px; background: #f1f5f9; border: 1px solid #cbd5e1; border-radius: 4px; cursor: pointer; width: 100%; text-align: left;"
                        data-name="${name}"
                      >
                        ${name}
                      </button>
                    </li>
                  `
                    )
                    .join("")}
                </ul>
                <p style="font-size: 0.9em; color: #718096; margin-top: 10px;">
                  Click a suggestion to use it, or enter your own custom name below.
                </p>
              </div>
              
              <div style="background: #e6fffa; padding: 15px; border-radius: 8px;">
                <p style="font-weight: 600; margin-bottom: 10px;">📝 Recommendation:</p>
                <p>To create this exam, please use a different name or select "Custom Exam" option and enter a unique name.</p>
                <p style="margin-top: 10px; font-size: 0.9em;">
                  <strong>Generated Exam ID:</strong> ${
                    formData.examId || "Not generated yet"
                  }
                </p>
              </div>
            </div>
          `,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Use Custom Name",
          cancelButtonText: "Cancel",
          confirmButtonColor: "#3182ce",
          cancelButtonColor: "#a0aec0",
          width: "650px",
          didOpen: () => {
            // Add click handlers for suggestion buttons
            document.querySelectorAll("[data-name]").forEach((button) => {
              button.addEventListener("click", (e) => {
                const suggestedName = e.target.getAttribute("data-name");
                setFormData((prev) => ({
                  ...prev,
                  examTypeOption: "custom",
                  customExamType: suggestedName,
                  examType: "",
                }));
                // Update the custom input if it exists
                const customInput = document.querySelector(
                  'input[name="customExamType"]'
                );
                if (customInput) {
                  customInput.value = suggestedName;
                }
              });
            });
          },
        }).then((result) => {
          if (result.isConfirmed) {
            // Switch to custom exam type
            setFormData((prev) => ({
              ...prev,
              examTypeOption: "custom",
            }));
            // Focus on custom exam type input
            setTimeout(() => {
              document.querySelector('input[name="customExamType"]')?.focus();
            }, 100);
          }
        });
      } else {
        // Exam is unique
        setVerificationResult({
          isDuplicate: false,
          message: "Exam details are unique and ready for submission.",
          examId: formData.examId || generateExamId(),
        });

        Swal.fire({
          title: "✅ Exam Details Verified",
          html: `
            <div style="text-align: left;">
              <div style="background: #f0fff4; padding: 15px; border-radius: 8px; border-left: 4px solid #48bb78; margin-bottom: 15px;">
                <p style="color: #48bb78; font-weight: 600;">
                  ✓ Exam details are unique and ready for submission.
                </p>
                <p><strong>Generated Exam ID:</strong> ${
                  formData.examId || generateExamId()
                }</p>
                <p><strong>Exam Details:</strong></p>
                <ul style="margin-left: 20px;">
                  <li>Type: ${
                    formData.examTypeOption === "custom"
                      ? formData.customExamType
                      : formData.examType
                  }</li>
                  <li>Subject: ${formData.subject}</li>
                  <li>Section: ${formData.classSection}</li>
                  <li>Date: ${formData.date}</li>
                  <li>Total Marks: ${formData.totalMarks}</li>
                </ul>
              </div>
              
              <div style="background: #f7fafc; padding: 12px; border-radius: 6px;">
                <p style="font-weight: 600; margin-bottom: 8px;">ℹ️ System Information:</p>
                <ul style="margin-left: 20px; font-size: 0.9em;">
                  <li>Unique Exam ID generated for tracking</li>
                  <li>No duplicate exams found for this year</li>
                  <li>Ready to proceed with student marks entry</li>
                </ul>
              </div>
            </div>
          `,
          icon: "success",
          confirmButtonText: "Continue",
          confirmButtonColor: "#48bb78",
        });
      }
    } catch (error) {
      console.error("Verification error:", error);
      Swal.fire({
        title: "Verification Failed",
        text: error.response?.data?.error || "Failed to verify exam details",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setIsVerifying(false);
    }
  };

  // Fetch subjects by class from PHP backend
  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        setApiStatus((prev) => ({ ...prev, subjects: "loading" }));
        const response = await axios.get(PHP_API_BASE, {
          params: {
            action: "get-subjects",
            className: className,
          },
        });

        if (response.data.success) {
          setSubjects(response.data.data || []);
          setApiStatus((prev) => ({ ...prev, subjects: "success" }));
        } else {
          console.warn("No subjects found for class:", className);
          setSubjects([]);
          setApiStatus((prev) => ({ ...prev, subjects: "empty" }));
        }
      } catch (error) {
        handleApiError(error, "fetch subjects");
        setSubjects([]);
        setApiStatus((prev) => ({ ...prev, subjects: "error" }));
      }
    };

    fetchSubjects();
  }, [className]);

  // Fetch sections by class from PHP backend
  useEffect(() => {
    const fetchSections = async () => {
      try {
        setApiStatus((prev) => ({ ...prev, sections: "loading" }));
        const response = await axios.get(PHP_API_BASE, {
          params: {
            action: "get-class-sections",
            className: className,
          },
        });

        if (response.data.success) {
          const sectionsFromDB = response.data.data || [];
          const studentSections = response.data.student_sections || [];

          const allSections = [
            ...new Set([
              ...sectionsFromDB.map((s) => s.section_hint || s.section_name),
              ...studentSections,
            ]),
          ].filter(Boolean);

          setSections(
            allSections.map((section) => ({
              id: section,
              section_hint: section,
              section_name: section,
            }))
          );
          setApiStatus((prev) => ({ ...prev, sections: "success" }));
        } else {
          console.warn("No sections found for class:", className);
          setSections([]);
          setApiStatus((prev) => ({ ...prev, sections: "empty" }));
        }
      } catch (error) {
        handleApiError(error, "fetch sections");
        setSections([]);
        setApiStatus((prev) => ({ ...prev, sections: "error" }));
      }
    };

    fetchSections();
  }, [className]);

  // Fetch students when section is selected
  useEffect(() => {
    const fetchStudents = async () => {
      if (!formData.classSection) {
        setStudents([]);
        return;
      }

      try {
        setIsLoading(true);
        setApiStatus((prev) => ({ ...prev, students: "loading" }));

        const response = await axios.get(PHP_API_BASE, {
          params: {
            action: "get-students-for-exam",
            className: className,
            section_name: formData.classSection,
          },
        });

        if (response.data.success) {
          const studentsData = response.data.data.map((student) => ({
            id: student.student_id || student.id,
            name: student.name,
            father_name: student.father_name,
            examType:
              formData.examTypeOption === "custom"
                ? formData.customExamType
                : formData.examType,
            class: className,
            section: formData.classSection,
            subject: formData.subject,
            totalMarks: parseFloat(formData.totalMarks) || 0,
            obtainedMarks: 0,
            percentage: 0,
            date: formData.date,
            day: formData.examDay,
            status: "pending",
            mobile: student.mobile,
            b_form_no: student.b_form_no,
            address: student.address,
            gender: student.gender,
            family_status: student.family_status,
          }));

          setStudents(studentsData);
          setApiStatus((prev) => ({ ...prev, students: "success" }));

          // Focus first input after loading
          setTimeout(() => {
            if (inputRefs.current[0]) {
              inputRefs.current[0].focus();
              inputRefs.current[0].select();
            }
          }, 100);
        } else {
          console.warn("No students found for section:", formData.classSection);
          setStudents([]);
          setApiStatus((prev) => ({ ...prev, students: "empty" }));
        }
      } catch (error) {
        handleApiError(error, "fetch students");
        setStudents([]);
        setApiStatus((prev) => ({ ...prev, students: "error" }));
      } finally {
        setIsLoading(false);
      }
    };

    const debounceTimer = setTimeout(() => {
      if (formData.classSection && formData.subject && formData.totalMarks) {
        fetchStudents();
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [
    className,
    formData.classSection,
    formData.subject,
    formData.totalMarks,
    formData.date,
    formData.examDay,
    formData.examType,
    formData.customExamType,
    formData.examTypeOption,
  ]);

  // Keyboard navigation effect
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (
        ["ArrowUp", "ArrowDown", "Enter"].includes(e.key) &&
        document.activeElement.className === "exam-sheet-marks-input"
      ) {
        e.preventDefault();
        const currentIndex = Array.from(
          document.querySelectorAll(".exam-sheet-marks-input")
        ).indexOf(document.activeElement);

        if (
          e.key === "ArrowDown" &&
          currentIndex < inputRefs.current.length - 1
        ) {
          const nextInput = inputRefs.current[currentIndex + 1];
          if (nextInput) {
            nextInput.focus();
            nextInput.select();
            setCurrentFocusIndex(currentIndex + 1);
          }
        } else if (e.key === "ArrowUp" && currentIndex > 0) {
          const prevInput = inputRefs.current[currentIndex - 1];
          if (prevInput) {
            prevInput.focus();
            prevInput.select();
            setCurrentFocusIndex(currentIndex - 1);
          }
        } else if (e.key === "Enter") {
          if (currentIndex < inputRefs.current.length - 1) {
            const nextInput = inputRefs.current[currentIndex + 1];
            if (nextInput) {
              nextInput.focus();
              nextInput.select();
              setCurrentFocusIndex(currentIndex + 1);
            }
          } else {
            inputRefs.current[0]?.focus();
            inputRefs.current[0]?.select();
            setCurrentFocusIndex(0);
          }
        }
      }
    };

    document.addEventListener("keydown", handleGlobalKeyDown);
    return () => document.removeEventListener("keydown", handleGlobalKeyDown);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleExamTypeChange = (e) => {
    const { value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      examTypeOption: value,
      examType: value === "predefined" ? prevData.examType : "",
      customExamType: value === "custom" ? prevData.customExamType : "",
    }));
  };

  const handleMarksUpdate = (e, studentId, index) => {
    const value = parseFloat(e.target.value) || 0;
    const totalMarks = parseFloat(formData.totalMarks) || 0;

    if (value > totalMarks) {
      Swal.fire({
        title: "Invalid Input",
        text: `Obtained marks cannot exceed ${totalMarks}.`,
        icon: "warning",
        confirmButtonText: "OK",
        customClass: { popup: "swal-popup" },
        showConfirmButton: true,
        timer: 1500,
      });

      if (inputRefs.current[index]) {
        inputRefs.current[index].focus();
        inputRefs.current[index].select();
      }
      return;
    }

    const updatedStudents = students.map((student) =>
      student.id === studentId
        ? {
            ...student,
            obtainedMarks: value,
            percentage:
              totalMarks > 0 ? ((value / totalMarks) * 100).toFixed(2) : 0,
            status: value > 0 ? "submitted" : "pending",
          }
        : student
    );
    setStudents(updatedStudents);
  };

  const handleBulkMarksUpdate = (marks) => {
    const totalMarks = parseFloat(formData.totalMarks) || 0;
    const updatedStudents = students.map((student) => ({
      ...student,
      obtainedMarks: marks,
      percentage: totalMarks > 0 ? ((marks / totalMarks) * 100).toFixed(2) : 0,
      status: marks > 0 ? "submitted" : "pending",
    }));
    setStudents(updatedStudents);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const requiredFields = ["subject", "totalMarks", "classSection", "date"];

    // Check exam type
    if (formData.examTypeOption === "custom" && !formData.customExamType) {
      Swal.fire({
        title: "Missing Information",
        text: "Please enter a custom exam name",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    if (formData.examTypeOption === "predefined" && !formData.examType) {
      Swal.fire({
        title: "Missing Information",
        text: "Please select an exam type",
        icon: "error",
        confirmButtonText: "OK",
      });
      return;
    }

    const missingFields = requiredFields.filter((field) => !formData[field]);
    if (missingFields.length > 0) {
      Swal.fire({
        title: "Missing Information",
        html: `
          <div style="text-align: left;">
            <p>Please fill in the following fields:</p>
            <ul style="margin-left: 20px;">
              ${missingFields
                .map(
                  (field) =>
                    `<li>${field.replace(/([A-Z])/g, " $1").trim()}</li>`
                )
                .join("")}
            </ul>
          </div>
        `,
        icon: "error",
        confirmButtonText: "OK",
        customClass: { popup: "swal-popup" },
      });
      return;
    }

    // Verify details before proceeding
    verifyExamDetails().then(() => {
      if (!verificationResult || verificationResult.isDuplicate) {
        // Don't proceed if duplicate
        return;
      }

      const examType =
        formData.examTypeOption === "custom"
          ? formData.customExamType
          : formData.examType;

      setExamDetails({
        examType: examType,
        subject: formData.subject,
        totalMarks: formData.totalMarks,
        classSection: formData.classSection,
        date: formData.date,
        examDay: formData.examDay,
        className: className,
        examId: formData.examId || generateExamId(),
        examTypeOption: formData.examTypeOption,
      });
      setIsModalOpen(false);
      setTimeout(() => {
        if (inputRefs.current[0]) {
          inputRefs.current[0].focus();
          inputRefs.current[0].select();
        }
      }, 300);
    });
  };

  const handleTableSubmit = async () => {
    if (students.length === 0) {
      Swal.fire({
        title: "No Data",
        text: "No student data to submit!",
        icon: "warning",
        confirmButtonText: "OK",
        customClass: { popup: "swal-popup" },
      });
      return;
    }

    const incompleteStudents = students.filter((s) => s.obtainedMarks === 0);
    if (incompleteStudents.length > 0) {
      const result = await Swal.fire({
        title: "Incomplete Data",
        html: `${incompleteStudents.length} student(s) have 0 marks. Continue anyway?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Submit Anyway",
        cancelButtonText: "Review",
        confirmButtonColor: "#00b09b",
        cancelButtonColor: "#6c757d",
        customClass: { popup: "swal-popup" },
      });
      if (!result.isConfirmed) return;
    }

    const examType =
      formData.examTypeOption === "custom"
        ? formData.customExamType
        : formData.examType;
    const examId = formData.examId || generateExamId();

    Swal.fire({
      title: "Final Submission",
      html: `
        <div style="text-align: left;">
          <p>This will save all exam marks to the database.</p>
          <p><strong>Exam Details:</strong></p>
          <ul style="margin-left: 20px;">
            <li>Exam Type: ${examType}</li>
            <li>Subject: ${formData.subject}</li>
            <li>Class: ${className}</li>
            <li>Section: ${formData.classSection}</li>
            <li>Date: ${formData.date}</li>
            <li>Total Students: ${students.length}</li>
            <li>Total Marks: ${formData.totalMarks}</li>
            <li><strong>Exam ID:</strong> ${examId}</li>
          </ul>
          <p>Continue?</p>
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Submit",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#6a11cb",
      cancelButtonColor: "#6c757d",
      customClass: { popup: "swal-popup" },
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setIsSubmitting(true);
          setApiStatus((prev) => ({ ...prev, submit: "loading" }));

          const payload = {
            examId: examId,
            examType: examType,
            subject: formData.subject,
            totalMarks: parseFloat(formData.totalMarks),
            class: className,
            classSection: formData.classSection,
            date: formData.date,
            examDay: formData.examDay,
            testResults: students.map((student) => ({
              studentId: student.id,
              studentName: student.name,
              obtainedMarks: parseFloat(student.obtainedMarks),
              percentage: parseFloat(student.percentage),
            })),
          };

          console.log("Submitting to PHP backend:", payload);

          const response = await fetch(
            `${PHP_API_BASE}?action=submit-exam-marks`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(payload),
            }
          );

          const resultData = await response.json();
          console.log("PHP Response:", resultData);

          if (resultData.success) {
            Swal.fire({
              title: "Success!",
              html: `
                <div style="text-align: left;">
                  <div style="background: #f0fff4; padding: 15px; border-radius: 8px; border-left: 4px solid #48bb78; margin-bottom: 15px;">
                    <p style="color: #48bb78; font-weight: 600;">
                      ✓ ${resultData.message}
                    </p>
                  </div>
                  <div style="background: #f7fafc; padding: 12px; border-radius: 6px;">
                    <p style="margin: 0 0 8px 0; font-weight: 600;">Submission Summary:</p>
                    <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px;">
                      <div style="color: #38a169;">
                        <span style="font-weight: 600;">✓ Students:</span> ${
                          resultData.stats?.successful || 0
                        }
                      </div>
                      <div style="color: #4a5568;">
                        <span style="font-weight: 600;">📝 Exam Type:</span> ${examType}
                      </div>
                      <div style="color: #4a5568;">
                        <span style="font-weight: 600;">🆔 Exam ID:</span> ${examId}
                      </div>
                      <div style="color: #4a5568;">
                        <span style="font-weight: 600;">📅 Date:</span> ${
                          formData.date
                        }
                      </div>
                      <div style="color: #4a5568;">
                        <span style="font-weight: 600;">📚 Subject:</span> ${
                          formData.subject
                        }
                      </div>
                      <div style="color: #4a5568;">
                        <span style="font-weight: 600;">🏫 Class:</span> ${className}
                      </div>
                      <div style="color: #4a5568;">
                        <span style="font-weight: 600;">👥 Section:</span> ${
                          formData.classSection
                        }
                      </div>
                    </div>
                  </div>
                </div>
              `,
              icon: "success",
              confirmButtonText: "OK",
              confirmButtonColor: "#00b09b",
              customClass: { popup: "swal-popup" },
            });
            setApiStatus((prev) => ({ ...prev, submit: "success" }));
            setSavedExams((prevExams) => [...prevExams, payload]);

            // Clear session storage and reset form after successful submission
            clearSavedData();
            resetForm();
          } else {
            throw new Error(resultData.error || "Submission failed");
          }
        } catch (error) {
          console.error("Error submitting data:", error);

          let errorMessage = error.message;
          if (error.response?.data?.error) {
            errorMessage = error.response.data.error;
          } else if (error.message?.includes("Duplicate")) {
            errorMessage =
              "Duplicate entry detected. This exam may already exist for the selected date, section, and subject.";
          }

          Swal.fire({
            title: "Submission Failed",
            html: `
              <div style="text-align: left;">
                <p style="color: #e53e3e; font-weight: 600;">${errorMessage}</p>
                <div style="background: #fff5f5; padding: 12px; border-radius: 6px; margin-top: 1rem; border-left: 4px solid #e53e3e;">
                  <p style="margin: 0 0 8px 0; font-weight: 600; color: #718096;">Troubleshooting Steps:</p>
                  <ul style="margin-left: 20px; color: #718096;">
                    <li>Check if PHP backend is running</li>
                    <li>Verify database connection is working</li>
                    <li>Ensure exam details are unique</li>
                    <li>Try verifying exam details again</li>
                  </ul>
                </div>
              </div>
            `,
            icon: "error",
            confirmButtonText: "OK",
            customClass: { popup: "swal-popup" },
          });
          setApiStatus((prev) => ({ ...prev, submit: "error" }));
        } finally {
          setIsSubmitting(false);
        }
      }
    });
  };

  const handleSendData = () => {
    if (students.length === 0) {
      Swal.fire({
        title: "No Data",
        text: "No student data to print!",
        icon: "warning",
        confirmButtonText: "OK",
      });
      return;
    }

    // Prepare the data to be sent
    const printData = {
      students,
      examDetails: {
        ...examDetails,
        className: className,
      },
    };

    // Log the data to the console
    console.log("=== Data sent to Print Exam Sheet ===");
    console.log("Timestamp:", new Date().toISOString());
    console.log("Exam Details:", printData.examDetails);
    console.log("Students Count:", students.length);
    console.log("First Student Sample:", students[0]); // Show first student as sample
    console.log("Full Data (JSON):", JSON.stringify(printData, null, 2));
    console.log("======================================");

    // Navigate to print page with the data
    navigate("/Print-Exam-Sheet-Sample", {
      state: printData,
    });
  };

  const filteredStudents = students.filter(
    (student) =>
      student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.id.toString().includes(searchTerm)
  );

  const calculateStatistics = () => {
    const totalStudents = students.length;
    const submittedStudents = students.filter(
      (s) => s.obtainedMarks > 0
    ).length;
    const totalMarks = students.reduce(
      (sum, s) => sum + parseFloat(s.obtainedMarks),
      0
    );
    const averageMarks = totalStudents > 0 ? totalMarks / totalStudents : 0;
    const highestMarks = Math.max(...students.map((s) => s.obtainedMarks));
    const lowestMarks = Math.min(
      ...students.filter((s) => s.obtainedMarks > 0).map((s) => s.obtainedMarks)
    );

    return {
      totalStudents,
      submittedStudents,
      averageMarks: averageMarks.toFixed(2),
      highestMarks,
      lowestMarks,
      completionRate:
        totalStudents > 0
          ? ((submittedStudents / totalStudents) * 100).toFixed(1)
          : 0,
    };
  };

  const stats = calculateStatistics();

  return (
    <div className="exam-sheet-screen">
      <div className="exam-sheet-dashboard-wrapper">
        <Sidebar />

        {/* Modal Overlay */}
        {isModalOpen && (
          <div className="exam-sheet-modal-overlay">
            <div className="exam-sheet-modal-container">
              <div className="exam-sheet-modal-header">
                <h2 className="exam-sheet-modal-title">
                  <FaClipboardCheck className="exam-sheet-modal-icon" />
                  Create New Exam - {className}
                </h2>
                <p className="exam-sheet-modal-subtitle">
                  Configure exam details and parameters
                </p>
                <button
                  className="exam-sheet-modal-close"
                  onClick={() => setIsModalOpen(false)}
                >
                  <FaTimes />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="exam-sheet-modal-form">
                <div className="exam-sheet-form-grid">
                  {/* Exam Type Selection */}
                  <div className="exam-sheet-form-group">
                    <label className="exam-sheet-form-label">
                      <span className="exam-sheet-label-text">Exam Type *</span>
                      <div className="exam-sheet-type-options">
                        <div className="exam-sheet-type-option">
                          <input
                            type="radio"
                            id="predefined"
                            name="examTypeOption"
                            value="predefined"
                            checked={formData.examTypeOption === "predefined"}
                            onChange={handleExamTypeChange}
                          />
                          <label htmlFor="predefined">Predefined Exam</label>
                        </div>
                        <div className="exam-sheet-type-option">
                          <input
                            type="radio"
                            id="custom"
                            name="examTypeOption"
                            value="custom"
                            checked={formData.examTypeOption === "custom"}
                            onChange={handleExamTypeChange}
                          />
                          <label htmlFor="custom">Custom Exam</label>
                        </div>
                      </div>
                    </label>
                  </div>

                  {/* Predefined Exam Type Selection */}
                  {formData.examTypeOption === "predefined" && (
                    <div className="exam-sheet-form-group">
                      <label className="exam-sheet-form-label">
                        <span className="exam-sheet-label-text">
                          Select Exam *
                        </span>
                        <div className="exam-sheet-select-wrapper">
                          <select
                            name="examType"
                            value={formData.examType}
                            onChange={handleInputChange}
                            className="exam-sheet-form-select"
                            required
                          >
                            <option value="">Select Exam Type</option>
                            {EXAM_TYPES.predefined.map((type) => (
                              <option key={type.id} value={type.value}>
                                {type.label}
                              </option>
                            ))}
                          </select>
                          <div className="exam-sheet-select-arrow">▼</div>
                        </div>
                        <div className="exam-sheet-type-info">
                          <FaInfoCircle />
                          <small>
                            Predefined exams can only be created once per year
                            per class-section-subject.
                          </small>
                        </div>
                      </label>
                    </div>
                  )}

                  {/* Custom Exam Type Input */}
                  {formData.examTypeOption === "custom" && (
                    <div className="exam-sheet-form-group">
                      <label className="exam-sheet-form-label">
                        <span className="exam-sheet-label-text">
                          Custom Exam Name *
                        </span>
                        <input
                          type="text"
                          name="customExamType"
                          value={formData.customExamType}
                          onChange={handleInputChange}
                          className="exam-sheet-form-input"
                          placeholder="Enter custom exam name (e.g., '1st Assessment Retake')"
                          required
                          autoComplete="off"
                        />
                        <div className="exam-sheet-type-info">
                          <FaInfoCircle />
                          <small>
                            Use custom names for retakes, makeup exams, or
                            special assessments.
                          </small>
                        </div>
                      </label>
                    </div>
                  )}

                  {/* Subject */}
                  <div className="exam-sheet-form-group">
                    <label className="exam-sheet-form-label">
                      <span className="exam-sheet-label-text">
                        Subject *
                        {apiStatus.subjects && (
                          <span
                            className="exam-sheet-status-badge"
                            style={{
                              backgroundColor: getStatusColor(
                                apiStatus.subjects
                              ),
                              marginLeft: "8px",
                              fontSize: "0.7em",
                              padding: "2px 6px",
                            }}
                          >
                            {getStatusText(apiStatus.subjects)}
                          </span>
                        )}
                      </span>
                      <div className="exam-sheet-select-wrapper">
                        <select
                          name="subject"
                          value={formData.subject}
                          onChange={handleInputChange}
                          className="exam-sheet-form-select"
                          required
                          disabled={
                            apiStatus.subjects === "loading" ||
                            apiStatus.subjects === "error"
                          }
                        >
                          <option value="">Select a Subject *</option>
                          {subjects.length === 0 ? (
                            <option value="" disabled>
                              {apiStatus.subjects === "loading"
                                ? "Loading subjects..."
                                : apiStatus.subjects === "error"
                                ? "Error loading subjects"
                                : "No subjects found"}
                            </option>
                          ) : (
                            subjects.map((subject) => (
                              <option key={subject.id} value={subject.name}>
                                {subject.name}{" "}
                                {subject.code ? `(${subject.code})` : ""}
                              </option>
                            ))
                          )}
                        </select>
                        <div className="exam-sheet-select-arrow">▼</div>
                      </div>
                    </label>
                  </div>

                  {/* Total Marks */}
                  <div className="exam-sheet-form-group">
                    <label className="exam-sheet-form-label">
                      <span className="exam-sheet-label-text">
                        Total Marks *
                      </span>
                      <input
                        type="number"
                        name="totalMarks"
                        value={formData.totalMarks}
                        onChange={handleInputChange}
                        className="exam-sheet-form-input"
                        min="0"
                        step="0.5"
                        placeholder="Enter total marks"
                        required
                      />
                    </label>
                  </div>

                  {/* Class Section */}
                  <div className="exam-sheet-form-group">
                    <label className="exam-sheet-form-label">
                      <span className="exam-sheet-label-text">
                        Class Section *
                        {apiStatus.sections && (
                          <span
                            className="exam-sheet-status-badge"
                            style={{
                              backgroundColor: getStatusColor(
                                apiStatus.sections
                              ),
                              marginLeft: "8px",
                              fontSize: "0.7em",
                              padding: "2px 6px",
                            }}
                          >
                            {getStatusText(apiStatus.sections)}
                          </span>
                        )}
                      </span>
                      <div className="exam-sheet-select-wrapper">
                        <select
                          name="classSection"
                          value={formData.classSection}
                          onChange={handleInputChange}
                          className="exam-sheet-form-select"
                          required
                          disabled={
                            apiStatus.sections === "loading" ||
                            apiStatus.sections === "error"
                          }
                        >
                          <option value="">Select a Section *</option>
                          {sections.length === 0 ? (
                            <option value="" disabled>
                              {apiStatus.sections === "loading"
                                ? "Loading sections..."
                                : apiStatus.sections === "error"
                                ? "Error loading sections"
                                : "No sections found"}
                            </option>
                          ) : (
                            sections.map((section) => (
                              <option
                                key={section.id}
                                value={section.section_hint}
                              >
                                {section.section_hint}
                              </option>
                            ))
                          )}
                        </select>
                        <div className="exam-sheet-select-arrow">▼</div>
                      </div>
                    </label>
                  </div>

                  {/* Date */}
                  <div className="exam-sheet-form-group">
                    <label className="exam-sheet-form-label">
                      <span className="exam-sheet-label-text">Exam Date *</span>
                      <input
                        type="date"
                        name="date"
                        value={formData.date}
                        onChange={handleInputChange}
                        className="exam-sheet-form-input"
                        required
                      />
                      <div className="exam-sheet-year-display">
                        Year: {new Date(formData.date).getFullYear()}
                      </div>
                    </label>
                  </div>

                  {/* Day */}
                  <div className="exam-sheet-form-group">
                    <label className="exam-sheet-form-label">
                      <span className="exam-sheet-label-text">
                        Day of Week *
                      </span>
                      <div className="exam-sheet-select-wrapper">
                        <select
                          name="examDay"
                          value={formData.examDay}
                          onChange={handleInputChange}
                          className="exam-sheet-form-select"
                          required
                        >
                          <option value="">Select Day</option>
                          {[
                            "Monday",
                            "Tuesday",
                            "Wednesday",
                            "Thursday",
                            "Friday",
                            "Saturday",
                            "Sunday",
                          ].map((day) => (
                            <option key={day} value={day}>
                              {day}
                            </option>
                          ))}
                        </select>
                        <div className="exam-sheet-select-arrow">▼</div>
                      </div>
                    </label>
                  </div>

                  {/* Generated Exam ID */}
                  <div className="exam-sheet-form-group">
                    <label className="exam-sheet-form-label">
                      <span className="exam-sheet-label-text">
                        <FaIdCard /> System Generated Exam ID
                      </span>
                      <div className="exam-sheet-exam-id-container">
                        <input
                          type="text"
                          value={formData.examId || "Not generated yet"}
                          readOnly
                          className="exam-sheet-form-input exam-id-input"
                          style={{ backgroundColor: "#f7fafc" }}
                        />
                        <button
                          type="button"
                          className="exam-sheet-generate-id-btn"
                          onClick={() => generateExamId()}
                        >
                          Generate ID
                        </button>
                      </div>
                      {formData.examId && (
                        <small className="exam-sheet-id-info">
                          ✓ Unique 12-character ID generated for system tracking
                        </small>
                      )}
                    </label>
                  </div>
                </div>

                {/* Verification Result */}
                {verificationResult && (
                  <div
                    className={`exam-sheet-verification-result ${
                      verificationResult.isDuplicate ? "duplicate" : "unique"
                    }`}
                  >
                    <div className="exam-sheet-verification-icon">
                      {verificationResult.isDuplicate ? (
                        <FaExclamationCircle />
                      ) : (
                        <FaCheckCircle />
                      )}
                    </div>
                    <div className="exam-sheet-verification-content">
                      <div className="exam-sheet-verification-title">
                        {verificationResult.isDuplicate
                          ? "Duplicate Detected"
                          : "Details Verified"}
                      </div>
                      <div className="exam-sheet-verification-message">
                        {verificationResult.message}
                      </div>
                      {verificationResult.examId && (
                        <div className="exam-sheet-generated-id">
                          Exam ID: <strong>{verificationResult.examId}</strong>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Existing Exams Warning */}
                {existingExams.length > 0 && (
                  <div className="exam-sheet-existing-exams-warning">
                    <div className="exam-sheet-warning-icon">
                      <FaExclamationTriangle />
                    </div>
                    <div className="exam-sheet-warning-content">
                      <div className="exam-sheet-warning-title">
                        Existing Exams Found
                      </div>
                      <div className="exam-sheet-warning-list">
                        <p>Similar exams already exist for this year:</p>
                        <ul>
                          {existingExams.slice(0, 3).map((exam, index) => (
                            <li key={index}>
                              {exam.exam_type} - {exam.exam_date} -{" "}
                              {exam.class_section}
                            </li>
                          ))}
                          {existingExams.length > 3 && (
                            <li>...and {existingExams.length - 3} more</li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                <div className="exam-sheet-modal-actions">
                  <button
                    type="button"
                    className="exam-sheet-modal-btn exam-sheet-modal-btn-info"
                    onClick={verifyExamDetails}
                    disabled={
                      isVerifying ||
                      !formData.subject ||
                      !formData.totalMarks ||
                      !formData.classSection ||
                      !formData.date ||
                      (formData.examTypeOption === "predefined" &&
                        !formData.examType) ||
                      (formData.examTypeOption === "custom" &&
                        !formData.customExamType)
                    }
                  >
                    {isVerifying ? (
                      <>
                        <FaSpinner className="exam-sheet-spin" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        <FaCheckCircle />
                        Verify Details
                      </>
                    )}
                  </button>
                  <button
                    type="submit"
                    className="exam-sheet-modal-btn exam-sheet-modal-btn-primary"
                    disabled={
                      !formData.subject ||
                      !formData.totalMarks ||
                      !formData.classSection ||
                      !formData.date ||
                      !formData.examDay ||
                      (formData.examTypeOption === "predefined" &&
                        !formData.examType) ||
                      (formData.examTypeOption === "custom" &&
                        !formData.customExamType) ||
                      (verificationResult && verificationResult.isDuplicate)
                    }
                  >
                    <FaClipboardCheck />
                    Create Exam & Load Students
                  </button>
                  <button
                    type="button"
                    className="exam-sheet-modal-btn exam-sheet-modal-btn-secondary"
                    onClick={() => setIsModalOpen(false)}
                  >
                    <FaTimes />
                    Cancel
                  </button>
                </div>

                {/* API Status Indicator */}
                <div className="exam-sheet-api-status">
                  <div className="exam-sheet-api-status-item">
                    <span>Subjects:</span>
                    <span
                      className="exam-sheet-status-dot"
                      style={{
                        backgroundColor: getStatusColor(apiStatus.subjects),
                      }}
                    ></span>
                    <small>{getStatusText(apiStatus.subjects)}</small>
                  </div>
                  <div className="exam-sheet-api-status-item">
                    <span>Sections:</span>
                    <span
                      className="exam-sheet-status-dot"
                      style={{
                        backgroundColor: getStatusColor(apiStatus.sections),
                      }}
                    ></span>
                    <small>{getStatusText(apiStatus.sections)}</small>
                  </div>
                </div>

                {/* Help Text */}
                <div className="exam-sheet-help-text">
                  <p>
                    <small>
                      <strong>Important:</strong> Predefined exams (like "1st
                      Assessment") can only be created once per year per
                      class-section-subject combination. Use "Custom Exam" for
                      retakes or special assessments.
                    </small>
                  </p>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Main Content Area */}
        <main className="exam-sheet-main-content">
          {/* Header Section */}
          <div className="exam-sheet-header">
            <div className="exam-sheet-header-content">
              <div className="exam-sheet-header-left">
                <h1 className="exam-sheet-main-title">
                  <FaCalculator className="exam-sheet-title-icon" />
                  Exam Marks Sheet
                </h1>
                <div className="exam-sheet-class-info">
                  <span className="exam-sheet-class-badge">{className}</span>
                  {examDetails && (
                    <>
                      <span className="exam-sheet-exam-type">
                        {examDetails.examType}
                        {examDetails.examTypeOption === "custom" && " (Custom)"}
                      </span>
                      <span className="exam-sheet-exam-subject">
                        {examDetails.subject}
                      </span>
                      <span className="exam-sheet-exam-section">
                        {examDetails.classSection}
                      </span>
                      <span className="exam-sheet-exam-id">
                        <FaIdCard /> {examDetails.examId || "No ID"}
                      </span>
                      <span className="exam-sheet-keyboard-badge">
                        <FaArrowUp />/<FaArrowDown /> Arrow Keys • Enter = Next
                      </span>
                    </>
                  )}
                </div>
              </div>

              <div className="exam-sheet-header-actions">
                <button
                  className="exam-sheet-action-btn exam-sheet-edit-btn"
                  onClick={() => setIsModalOpen(true)}
                >
                  <FaEdit />
                  Edit Exam Details
                </button>
                <button
                  className="exam-sheet-action-btn exam-sheet-print-btn"
                  onClick={handleSendData}
                  disabled={students.length === 0}
                >
                  <FaPrint />
                  Print Sheet
                </button>
                <button
                  className="exam-sheet-action-btn exam-sheet-submit-btn"
                  onClick={handleTableSubmit}
                  disabled={
                    isSubmitting || students.length === 0 || !formData.examId
                  }
                >
                  {isSubmitting ? (
                    <>
                      <FaSpinner className="exam-sheet-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <FaPaperPlane />
                      Submit All Marks
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Show/Hide Toggle for Stats & Controls */}
            <div className="exam-sheet-toggle-container" onClick={() => setShowStats(!showStats)}>
              <span className="exam-sheet-toggle-text">
                {showStats ? "Hide Dashboard Stats" : "Show Dashboard Stats"}
              </span>
              <div className={`exam-sheet-toggle-switch ${showStats ? "active" : ""}`}>
                <div className="exam-sheet-toggle-slider"></div>
              </div>
            </div>

            {/* Statistics Grid */}
            {showStats && (
              <>
                <div className="exam-sheet-stats-grid">
                  <div className="exam-sheet-stat-card total">
                    <div className="exam-sheet-stat-icon total">
                      <FaUserGraduate />
                    </div>
                    <div className="exam-sheet-stat-content">
                      <div className="exam-sheet-stat-value">
                        {stats.totalStudents}
                      </div>
                      <div className="exam-sheet-stat-label">Total Students</div>
                    </div>
                  </div>

                  <div className="exam-sheet-stat-card submitted">
                    <div className="exam-sheet-stat-icon submitted">✓</div>
                    <div className="exam-sheet-stat-content">
                      <div className="exam-sheet-stat-value">
                        {stats.submittedStudents}
                      </div>
                      <div className="exam-sheet-stat-label">Marks Submitted</div>
                    </div>
                  </div>

                  <div className="exam-sheet-stat-card avg">
                    <div className="exam-sheet-stat-icon avg">📊</div>
                    <div className="exam-sheet-stat-content">
                      <div className="exam-sheet-stat-value">
                        {stats.averageMarks}
                      </div>
                      <div className="exam-sheet-stat-label">Average Marks</div>
                    </div>
                  </div>

                  <div className="exam-sheet-stat-card high">
                    <div className="exam-sheet-stat-icon high">🏆</div>
                    <div className="exam-sheet-stat-content">
                      <div className="exam-sheet-stat-value">
                        {stats.highestMarks}
                      </div>
                      <div className="exam-sheet-stat-label">Highest Marks</div>
                    </div>
                  </div>
                </div>

                {/* Controls Section */}
                <div className="exam-sheet-controls-section">
                  <div className="exam-sheet-search-box">
                    <FaSearch className="exam-sheet-search-icon" />
                    <input
                      type="text"
                      placeholder="Search students by name or ID..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="exam-sheet-search-input"
                      disabled={students.length === 0}
                    />
                    {searchTerm && (
                      <button
                        className="exam-sheet-clear-search"
                        onClick={() => setSearchTerm("")}
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  <div className="exam-sheet-bulk-actions">
                    <span className="exam-sheet-bulk-label">Quick Fill:</span>
                    <input
                      type="number"
                      placeholder="Enter marks"
                      className="exam-sheet-bulk-input"
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          const marks = parseFloat(e.target.value);
                          if (!isNaN(marks)) {
                            handleBulkMarksUpdate(marks);
                            e.target.value = "";
                            if (inputRefs.current[0]) {
                              inputRefs.current[0].focus();
                              inputRefs.current[0].select();
                            }
                          }
                        }
                      }}
                      disabled={students.length === 0}
                    />
                    <span className="exam-sheet-keyboard-shortcut">
                      Enter to apply
                    </span>
                  </div>

                  <div className="exam-sheet-action-buttons">
                    <button
                      className="exam-sheet-action-btn exam-sheet-print-btn"
                      onClick={handleSendData}
                      disabled={students.length === 0}
                    >
                      <FaPrint /> Print Preview
                    </button>
                    <button
                      className="exam-sheet-action-btn exam-sheet-submit-btn"
                      onClick={handleTableSubmit}
                      disabled={
                        isSubmitting || students.length === 0 || !formData.examId
                      }
                    >
                      {isSubmitting ? (
                        <>
                          <FaSpinner className="exam-sheet-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <FaPaperPlane /> Submit All Marks
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="exam-sheet-loading-overlay">
              <div className="exam-sheet-loading-spinner">
                <FaSpinner className="exam-sheet-spin" size={32} />
                <p>Loading students from PHP backend...</p>
              </div>
            </div>
          )}

          {/* Table Container - Scrollable Area */}
          <div className="exam-sheet-table-container">
            <div className="exam-sheet-table-wrapper">
              <table className="exam-sheet-table">
                <thead className="exam-sheet-table-header">
                  <tr>
                    <th className="exam-sheet-th exam-sheet-th-sticky">
                      Student ID
                    </th>
                    <th className="exam-sheet-th exam-sheet-th-sticky">
                      Student Name
                    </th>
                    <th className="exam-sheet-th">Father's Name</th>
                    <th className="exam-sheet-th">Section</th>
                    <th className="exam-sheet-th">Total Marks</th>
                    <th className="exam-sheet-th exam-sheet-th-obtained">
                      Obtained Marks
                    </th>
                    <th className="exam-sheet-th">Percentage</th>
                    <th className="exam-sheet-th">Status</th>
                  </tr>
                </thead>

                <tbody className="exam-sheet-table-body">
                  {filteredStudents.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="exam-sheet-empty-cell">
                        {isLoading ? (
                          <div className="exam-sheet-loading-inline">
                            <FaSpinner className="exam-sheet-spin" /> Loading
                            students...
                          </div>
                        ) : (
                          <div className="exam-sheet-empty-state">
                            <FaExclamationTriangle className="exam-sheet-empty-icon" />
                            <div>
                              <p>
                                {students.length === 0
                                  ? "No students loaded. Please create an exam first."
                                  : "No students match your search."}
                              </p>
                              <small>
                                {students.length === 0
                                  ? "Fill in exam details and click 'Create Exam & Load Students'"
                                  : "Try a different search term"}
                              </small>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  ) : (
                    filteredStudents.map((student, index) => (
                      <tr
                        key={student.id}
                        className={`exam-sheet-table-row ${
                          currentFocusIndex === index
                            ? "exam-sheet-row-focused"
                            : ""
                        } ${student.status}`}
                        onClick={() => {
                          if (inputRefs.current[index]) {
                            inputRefs.current[index].focus();
                            inputRefs.current[index].select();
                          }
                        }}
                      >
                        <td className="exam-sheet-td exam-sheet-td-sticky">
                          {student.id}
                        </td>
                        <td className="exam-sheet-td exam-sheet-td-sticky">
                          {student.name}
                        </td>
                        <td className="exam-sheet-td">{student.father_name}</td>
                        <td className="exam-sheet-td">{student.section}</td>
                        <td className="exam-sheet-td">{student.totalMarks}</td>
                        <td className="exam-sheet-td">
                          <div className="exam-sheet-input-container">
                            <input
                              ref={(el) => (inputRefs.current[index] = el)}
                              type="number"
                              className="exam-sheet-marks-input"
                              value={student.obtainedMarks || ""}
                              onChange={(e) =>
                                handleMarksUpdate(e, student.id, index)
                              }
                              min="0"
                              max={formData.totalMarks}
                              step="0.5"
                              placeholder="Enter marks"
                              onFocus={() => setCurrentFocusIndex(index)}
                              onBlur={() => setCurrentFocusIndex(-1)}
                              onKeyDown={(e) => {
                                if (
                                  ["ArrowUp", "ArrowDown", "Enter"].includes(
                                    e.key
                                  )
                                ) {
                                  e.preventDefault();
                                }
                              }}
                            />
                            <div className="exam-sheet-input-hint">
                              Max: {formData.totalMarks}
                            </div>
                            <div className="exam-sheet-keyboard-hint">
                              ↑↓ Enter
                            </div>
                          </div>
                        </td>
                        <td className="exam-sheet-td">
                          <span
                            className={`exam-sheet-percentage ${
                              student.percentage >= 50
                                ? "exam-sheet-percentage-good"
                                : "exam-sheet-percentage-low"
                            }`}
                          >
                            {student.percentage}%
                          </span>
                        </td>
                        <td className="exam-sheet-td">
                          <span
                            className={`exam-sheet-status-badge exam-sheet-status-${student.status}`}
                          >
                            {student.status === "submitted"
                              ? "✓ Submitted"
                              : "⏳ Pending"}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer Summary */}
            {students.length > 0 && (
              <div className="exam-sheet-footer-summary">
                <div className="exam-sheet-summary-content">
                  <div className="exam-sheet-progress-summary">
                    <div className="exam-sheet-progress-bar">
                      <div
                        className="exam-sheet-progress-fill"
                        style={{ width: `${stats.completionRate}%` }}
                      ></div>
                    </div>
                    <div className="exam-sheet-progress-text">
                      {stats.completionRate}% Complete (
                      {stats.submittedStudents}/{stats.totalStudents})
                    </div>
                    <div className="exam-sheet-keyboard-tips">
                      <span className="exam-sheet-tip">
                        <FaArrowUp /> Arrow Up = Previous
                      </span>
                      <span className="exam-sheet-tip">
                        <FaArrowDown /> Arrow Down = Next
                      </span>
                      <span className="exam-sheet-tip">Enter = Next Row</span>
                    </div>
                    <div className="exam-sheet-exam-id-display">
                      Exam ID:{" "}
                      <strong>{formData.examId || "Not generated"}</strong>
                    </div>
                  </div>
                  <div className="exam-sheet-summary-actions">
                    <button
                      className="exam-sheet-action-btn exam-sheet-edit-btn"
                      onClick={() => {
                        if (inputRefs.current[0]) {
                          inputRefs.current[0].focus();
                          inputRefs.current[0].select();
                        }
                      }}
                    >
                      Focus First Input
                    </button>
                    <button
                      className="exam-sheet-action-btn exam-sheet-submit-btn"
                      onClick={handleTableSubmit}
                      disabled={isSubmitting || !formData.examId}
                    >
                      {isSubmitting ? (
                        <>
                          <FaSpinner className="exam-sheet-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <FaPaperPlane /> Final Submit
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ExamSheet;